import { useEffect } from "react";
import { trackPageView } from "@/lib/tracker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Hero from "@/components/sections/Hero";
import StatsBar from "@/components/sections/StatsBar";
import ProductSelector from "@/components/sections/ProductSelector";
import WhyEclipse from "@/components/sections/WhyEclipse";
import FeaturedProjects from "@/components/sections/FeaturedProjects";
import ReferenceClients from "@/components/sections/ReferenceClients";
import Testimonial from "@/components/sections/Testimonial";
import FinalCTA from "@/components/sections/FinalCTA";
import BackToTop from "@/components/BackToTop";
import FloatingPhone from "@/components/FloatingPhone";
import StickyMobileCTABar from "@/components/StickyMobileCTABar";
import InlineCTABanner from "@/components/InlineCTABanner";
import VideoSection from "@/components/VideoSection";
import YouTubeShortsRow from "@/components/YouTubeShortsRow";
import videos from "@/data/videos.json";
import { applySEO, orgSchema, localBusinessSchema, websiteSchema, breadcrumbSchema, videoObjectSchema } from "@/lib/seo";

const WA_HOME = "https://wa.me/528116289267?text=" + encodeURIComponent("Hola, me interesa cotizar un proyecto con Eclipse Cubiertas.");

export default function Home() {
    useEffect(() => {
        applySEO({
            path: "/",
            title: "Eclipse Cubiertas | Malla Sombra, Velarias y Tensoestructuras en Monterrey México",
            description: "Líderes en cubiertas textiles. Premio IFAI 2021. +10,000 techos instalados en México. Cotización gratis 24h. FlexShade modular y Skycover a la medida.",
            keywords: "cubiertas textiles, malla sombra Monterrey, velarias México, tensoestructuras, FlexShade, Skycover, Eclipse Cubiertas, arquitectura textil",
            ogImage: "/og/og-eclipse.png",
            ogImageAlt: "Eclipse Cubiertas — Líderes en malla sombra, velarias y tensoestructuras en México",
            jsonLD: [
                orgSchema,
                localBusinessSchema,
                websiteSchema,
                breadcrumbSchema([{ name: "Inicio", path: "/" }]),
                videoObjectSchema({
                    name: videos.eclipse_showcase[0].title,
                    description: "Diseño y soluciones en malla sombra y velarias en Monterrey por Eclipse Cubiertas.",
                    videoId: videos.eclipse_showcase[0].videoId,
                }),
            ],
        });
        trackPageView();
    }, []);

    return (
        <div className="min-h-screen bg-white text-eclipse-primary" data-testid="home-page">
            <Navbar variant="overlay" />
            <main>
                <Hero />

                {/* SEO sub-hero copy block */}
                <section data-testid="home-seo-intro" className="bg-white py-12 sm:py-16 border-b border-neutral-100">
                    <div className="container-eclipse max-w-4xl">
                        <p className="text-base sm:text-lg text-eclipse-muted leading-relaxed">
                            <span className="font-display font-bold text-eclipse-primary">Eclipse Cubiertas</span> es la firma mexicana especializada en
                            arquitectura textil con más de 20 años fabricando e instalando malla sombra, velarias y tensoestructuras. Operamos
                            con dos divisiones: <span className="font-semibold text-eclipse-primary">FlexShade</span> para sistemas modulares prefabricados sin permiso ni soldadura,
                            y <span className="font-semibold text-eclipse-primary">Skycover</span> para proyectos arquitectónicos premium a la medida con ingeniería estructural incluida.
                            Cobertura nacional desde Monterrey hasta CDMX, Guadalajara, Mérida y Cancún.
                        </p>
                    </div>
                </section>

                <StatsBar />
                <ProductSelector />
                <WhyEclipse />
                <FeaturedProjects />

                <VideoSection
                    eyebrow="Eclipse en acción"
                    title="Conoce nuestros proyectos en video"
                    subtitle="Más de 10,000 techos instalados en México — Premio IFAI 2021"
                    videoId={videos.eclipse_showcase[0].videoId}
                    videoTitle={videos.eclipse_showcase[0].title}
                    bullets={[
                        "Diseño, fabricación e instalación con equipo propio",
                        "Materiales importados Dickson, Sioen, Serge Ferrari",
                        "Cobertura nacional en más de 30 ciudades",
                        "Garantía formal por escrito en cada proyecto",
                    ]}
                    primaryCtaLabel="Ver productos"
                    onPrimaryCta={() => document.getElementById("productos")?.scrollIntoView({ behavior: "smooth" })}
                    sectionId="eclipse-video"
                    testId="home-video-section"
                />

                <YouTubeShortsRow
                    shorts={videos.shorts}
                    title="Mas videos en nuestro canal"
                    subtitle="Shorts de proyectos Eclipse Cubiertas"
                    testId="home-shorts-row"
                />

                <InlineCTABanner
                    heading="¿Tienes un proyecto entre manos?"
                    subheading="Cotización gratuita en menos de 24h. Ingenieros certificados. Garantía formal por escrito."
                    whatsappUrl={WA_HOME}
                    tel="+528189899822"
                    onCtaClick={() => document.getElementById("productos")?.scrollIntoView({ behavior: "smooth" })}
                    ctaLabel="Ver productos"
                    variant="dark"
                    testId="home-mid-banner"
                />

                <ReferenceClients />
                <Testimonial />
                <FinalCTA />
            </main>
            <Footer currentSite="eclipse" />

            <StickyMobileCTABar
                whatsappUrl={WA_HOME}
                ctaHref="tel:+528189899822"
                ctaLabel="Llamar"
                testId="home-sticky-cta"
            />
            <BackToTop testId="home-back-to-top" />
            <FloatingPhone tel="+528189899822" testId="home-floating-phone" />
        </div>
    );
}
